import React, { useEffect, useState } from "react";
import { api, PROCESS_TIMES, loadProcessTimes, ptColor } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Timer, Plus, Trash2, Save } from "lucide-react";

export const ProcessTimeSettings = ({ isAdmin }) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadProcessTimes().then((p) => setItems(p.map((x) => ({ ...x })))); }, []);

  const set = (i, k, v) => setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = () => setItems((arr) => [...arr, { value: "", turnaround: "", surcharge: 0, hours: 0 }]);
  const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = items.map((x) => ({ value: x.value.trim(), turnaround: x.turnaround, surcharge: Number(x.surcharge) || 0, hours: Number(x.hours) || 0 }));
      const { data } = await api.put("/process-times", { items: payload });
      PROCESS_TIMES.splice(0, PROCESS_TIMES.length, ...data);
      setItems(data.map((x) => ({ ...x })));
      toast.success("Process times saved");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="process-time-settings">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display font-bold flex items-center gap-2"><Timer className="w-4 h-4" /> Agent Process Time & Surcharge</h3>
          <p className="text-xs text-muted-foreground">Applies to all agent orders. Surcharge is a percentage added on top of the agent price. Hours = offset used to compute the target date.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={add} data-testid="pt-add"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="pt-save"><Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}</Button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-[1fr_1fr_100px_90px_auto] gap-2 text-xs font-mono uppercase text-muted-foreground px-1">
        <span>Name</span><span>Turnaround</span><span>Surcharge %</span><span>Hours</span><span />
      </div>
      {items.map((p, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_100px_90px_auto] gap-2 items-center" data-testid={`pt-row-${i}`}>
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full border shrink-0 ${ptColor(i)}`} />
            <Input value={p.value} disabled={!isAdmin} onChange={(e) => set(i, "value", e.target.value)} data-testid={`pt-name-${i}`} />
          </div>
          <Input value={p.turnaround} disabled={!isAdmin} onChange={(e) => set(i, "turnaround", e.target.value)} placeholder="e.g. 3 Days" data-testid={`pt-turnaround-${i}`} />
          <Input type="number" min="0" step="1" value={p.surcharge} disabled={!isAdmin} onChange={(e) => set(i, "surcharge", e.target.value)} data-testid={`pt-surcharge-${i}`} />
          <Input type="number" min="0" step="1" value={p.hours} disabled={!isAdmin} onChange={(e) => set(i, "hours", e.target.value)} data-testid={`pt-hours-${i}`} />
          {isAdmin ? <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => remove(i)} data-testid={`pt-remove-${i}`}><Trash2 className="w-4 h-4" /></Button> : <span />}
        </div>
      ))}
    </Card>
  );
};
